<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { format } from 'date-fns-tz'

import { useEpgAirTimeHelper } from '../../../../composables/use-epg-air-time-helper'
import { useImgSrcSet } from '../../../../composables/use-img-src-set'
import VodEpgBadges from '../../../../components/shared/vod-epg/Badges.vue'
import { usePageStore } from '../../../../stores/page'
import { useCdnImages } from '../../../../composables/use-cdn-images'

import type { EpgWidgetParagraph } from '../../../../utils/paragraph-schema/paragraphs'

type EpgWidget = EpgWidgetParagraph['epgWidget']
type EpgResultSet = NonNullable<EpgWidget['initialBroadcasts']>[number]
type Broadcast = EpgResultSet['broadcasts'][number]

const props = defineProps<{
  broadcast?: Broadcast
}>()

const { forceDateIntoTimeZone, displayAirDate } = useEpgAirTimeHelper()
const { createImgSrc } = useImgSrcSet()
const { cdnImageUrl } = useCdnImages()
const pageStore = usePageStore()

const progress = ref(-1) // -1 means not yet calculated
const onAir = ref(false) // true if the broadcast is currently on air

const showPlayIcon = computed(() => props.broadcast?.hasAutoPlay)

const thumbnailUrl = computed(() => createImgSrc({
  image: props.broadcast?.image ?? {},
  brand: pageStore.brandFromStore,
  width: 120,
  setName: 'broadcastThumbnail',
  type: 'webp'
}) ?? undefined)

const ariaLabel = computed(() => {
  const airTime = props.broadcast?.airTime
  const title = props.broadcast?.title
  if (airTime && title === displayAirDate(airTime)) {
    return `Wiederholung am ${title} um ${formatTime(airTime)} Uhr`
  }
  return title
})

const timeRangeLabel = computed(() => {
  const airTime = props.broadcast?.airTime
  const airTimeEnd = props.broadcast?.airTimeEnd
  if (!airTime || !airTimeEnd) return ''
  return `${formatTime(airTime)}-${formatTime(airTimeEnd)}`
})

function formatTime (timestamp: string) {
  const date = new Date(timestamp)

  // explicitly convert to our timezone to ensure consistent display
  // example: a request from san francisco (google) won't be 11:15 am but 20:15 - https://bauerxcel.atlassian.net/browse/NXT-8158
  const airTime = forceDateIntoTimeZone(date)

  return format(airTime, 'HH:mm')
}

function updateProgress () {
  const airTime = props.broadcast?.airTime
  const airTimeEnd = props.broadcast?.airTimeEnd
  if (!airTime || !airTimeEnd) return

  const now = Date.now()
  const start = new Date(airTime).getTime()
  const end = new Date(airTimeEnd).getTime()

  progress.value = now < start
    ? 0
    : now >= end
      ? 100
      : ((now - start) / (end - start)) * 100

  onAir.value = now >= start && now <= end
}

function onImageError (event: Event) {
  (event.target as HTMLImageElement).src = cdnImageUrl('/images/tvmovie/epgThumbnailFallback.jpeg')
}

onMounted(() => {
  updateProgress()
})
</script>

<template>
  <a :href="broadcast?.url"
     class="bx-epg-broadcast"
     :class="{
       'bx-epg-broadcast--on-air': onAir
     }"
     :aria-label="ariaLabel">
    <figure v-if="broadcast?.image">
      <img :src="thumbnailUrl"
           :alt="`${broadcast?.title} Thumbnail`"
           width="60"
           height="60"
           loading="lazy"
           @error="onImageError">
    </figure>
    <div>
      <div>
        <span v-if="broadcast?.categoryName">{{ broadcast.categoryName }}</span>
        <span v-if="broadcast?.genreName">{{ broadcast.genreName }}</span>
      </div>
      <div>{{ broadcast?.title }}</div>
      <div>
        <div v-if="onAir"
             :style="{ width: progress + '%' }" />
      </div>
      <div>
        <span>{{ timeRangeLabel }}</span>
        <span>- {{ displayAirDate(broadcast?.airTime, 'EEE dd.MM.') }}</span>
        <VodEpgBadges :badges="broadcast?.flags"
                      :inline-usage="true" />
        <div v-if="showPlayIcon"
             class="bx-badges bx-badges--below-teaser-content">
          <svg class="bx-badge bx-badge--video icon icon-play">
            <use href="#play" />
          </svg>
        </div>
      </div>
    </div>
  </a>
</template>
